/**
 * A sheet of die-cut labels, as numbers (PLAN-LABELS D-LB1).
 *
 * The paper is the brewer's; all this app has to do is land ink on the
 * die-cuts. That makes a label sheet a small pile of measurements rather than a
 * layout, so it lives here as data and every geometry question is answered by a
 * pure function over it. The URL, the dialog, the sheet component and the print
 * page all take a `LabelSheetSpec` rather than assuming one, which is what
 * makes the owner's eventual round sheet for bottle caps a second entry in this
 * file and a second label layout rather than a second print surface.
 *
 * ## The one entry, and where its numbers come from
 *
 * Avery 5520 is the weatherproof sheet a brewer keeps in the drawer, and 5160
 * is the same die-cut in plain paper, so one spec covers both. Avery quotes the
 * sheet in inches and the millimetres below are those inches exactly:
 *
 * - label 1″ × 2⅝″ → 25.4 × 66.675 mm
 * - top and bottom margin ½″ → 12.7 mm
 * - side margin 3⁄16″ → 4.7625 mm
 * - between columns ⅛″ → 3.175 mm, so the column pitch is 2¾″ → 69.85 mm
 * - no gap between rows, so the row pitch is the label's own 1″ → 25.4 mm
 * - US Letter 8½″ × 11″ → 215.9 × 279.4 mm
 *
 * The two margins are not independent of the rest: three columns at that pitch
 * end 4.7625 mm from the right edge and ten rows end 12.7 mm from the bottom,
 * which is the arithmetic the tests pin, because a sheet whose last column
 * walks off the paper is the one failure mode that wastes real stock.
 *
 * ## Percentages, not millimetres, out of `cellFrame`
 *
 * A cell's frame is returned as a percentage of the page so the one set of
 * numbers serves both sizes the sheet is drawn at (D-LB2): a box scaled to fit
 * a dialog on a phone, and a box that is exactly the page in millimetres on
 * paper. Millimetres would have to be divided by the page again at every call
 * site, and the two call sites would be free to disagree. Four decimals is a
 * thousandth of a millimetre on Letter, well under what any printer resolves,
 * and it keeps the inline styles readable in a snapshot.
 *
 * ## Cells are numbered from one
 *
 * Left to right, then top to bottom, 1 to 30 — because that is how a brewer
 * counts the sheet in their hand, and the URL that carries a print run is read
 * by a person (D-LB5). Rows and columns, where they appear at all, are the
 * ordinary zero-based kind; `cellIndex` is the one place the two meet.
 */

/** The sheets this app knows how to print. One, for now (D-LB1). */
export type LabelSheetId = 'avery-5520';

/** The label's outline, and all this plan does about it (D-LB2). */
export type LabelShape = 'rect' | 'round';

/** Every measurement a sheet of die-cut labels has, in millimetres. */
export interface LabelSheetSpec {
  readonly id: LabelSheetId;
  /** The paper this sheet is die-cut on. */
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly columns: number;
  readonly rows: number;
  readonly labelWidthMm: number;
  readonly labelHeightMm: number;
  /** From the top edge of the paper to the top edge of the first row. */
  readonly topMarginMm: number;
  /** From the left edge of the paper to the left edge of the first column. */
  readonly leftMarginMm: number;
  /** Left edge to left edge: the label's width plus whatever gap follows it. */
  readonly columnPitchMm: number;
  /** Top edge to top edge, which on this sheet is the label's own height. */
  readonly rowPitchMm: number;
  readonly shape: LabelShape;
}

/** Avery 5520 (and 5160, the same die-cut in plain paper). @see the header */
export const AVERY_5520: LabelSheetSpec = {
  id: 'avery-5520',
  pageWidthMm: 215.9,
  pageHeightMm: 279.4,
  columns: 3,
  rows: 10,
  labelWidthMm: 66.675,
  labelHeightMm: 25.4,
  topMarginMm: 12.7,
  leftMarginMm: 4.7625,
  columnPitchMm: 69.85,
  rowPitchMm: 25.4,
  shape: 'rect',
};

/** Every known sheet, by the id the URL names it with. */
export const LABEL_SHEETS: Readonly<Record<LabelSheetId, LabelSheetSpec>> = {
  'avery-5520': AVERY_5520,
};

/** What a URL that names no sheet, or names one we do not know, gets. */
export const DEFAULT_LABEL_SHEET: LabelSheetSpec = AVERY_5520;

/** Where one cell sits on the page, as percentages of it. @see the header */
export interface LabelCellFrame {
  readonly leftPercent: number;
  readonly topPercent: number;
  readonly widthPercent: number;
  readonly heightPercent: number;
}

/** How many labels the sheet holds — thirty, on the one sheet there is. */
export function cellCount(spec: LabelSheetSpec): number {
  return spec.columns * spec.rows;
}

/**
 * The 1-based number of the cell at a zero-based row and column.
 *
 * Left to right then top to bottom, so on a three-wide sheet the second row
 * starts at 4. The dialog and the print page both deal in cell numbers alone;
 * this exists for the code that walks the grid, and for the tests that check
 * the numbering runs the way a brewer reads the paper.
 */
export function cellIndex(spec: LabelSheetSpec, row: number, column: number): number {
  return row * spec.columns + column + 1;
}

/**
 * Where cell `index` (1-based) sits, in percentages of the page.
 *
 * Out-of-range numbers are not rejected here: callers hold the range already —
 * the parser drops a cell the sheet does not have (D-LB5) and the sheet
 * component only ever walks `cellCount` — and a geometry helper that throws
 * would make every one of them guard a thing they had already checked.
 */
export function cellFrame(spec: LabelSheetSpec, index: number): LabelCellFrame {
  const row = Math.floor((index - 1) / spec.columns);
  const column = (index - 1) % spec.columns;
  return {
    leftPercent: percent(spec.leftMarginMm + column * spec.columnPitchMm, spec.pageWidthMm),
    topPercent: percent(spec.topMarginMm + row * spec.rowPitchMm, spec.pageHeightMm),
    widthPercent: percent(spec.labelWidthMm, spec.pageWidthMm),
    heightPercent: percent(spec.labelHeightMm, spec.pageHeightMm),
  };
}

/** `mm` as a percentage of `ofMm`, to the four decimals the header argues for. */
function percent(mm: number, ofMm: number): number {
  return Math.round((mm / ofMm) * 1_000_000) / 10_000;
}
